@if (createTransactionReviewData$ | async; as reviewData) {
  @if (showApprovalSummary && approvalRequestId) {
    <div>
      <div class="modal-icon modal-icon-info"></div>
      <div class="modal-title">Approval Required</div>
      <div class="transaction-id">Approval Request # {{ approvalRequestId | rktShortId }}</div>
    </div>
    <div class="separator-line"></div>
    <div class="approval-message">This transaction exceeds your assigned limits and requires approval before it can be processed.</div>
    <div class="separator-line"></div>
    <div class="amount">
      <ng-container>
        @if (reviewData.amountOption === 'EXACT_AMOUNT') {
          <div class="label">Amount</div>
          <div class="value">
            {{ reviewData.amount || '' | amount: reviewData.currency || undefined }}
          </div>
        }
        @if (reviewData.amountOption === 'PERCENTAGE') {
          <div class="label">Percentage</div>
          <div class="value">{{ reviewData.amount }}%</div>
        }
      </ng-container>
      @if (reviewData.name) {
        <div class="scheduler-name">
          {{ reviewData.name }}
        </div>
      }
    </div>
    @if (reviewData.scheduleDateTime) {
      <div class="schedule-preview">
        <div class="scheduled-for">
          <div class="schedule-icon"></div>
          <div class="scheduled-for-label">Scheduled for:</div>
          <span class="scheduled-date-time">{{ reviewData.scheduleDateTimeDisplayValue }}</span>
        </div>
        @if (reviewData.scheduleRecurringDisplayValue; as scheduleRecurring) {
          <div class="schedule-recurring">Recurrence: {{ scheduleRecurring }}</div>
        }
      </div>
    }
    <div class="financial-accounts-container">
      <ng-container
        *ngTemplateOutlet="
          financialAccountView;
          context: {
            $implicit: reviewData.fromAccount,
            method: reviewData.fromMethod,
            achDetails: reviewData.fromAchDetails,
            wireDetails: reviewData.fromWireDetails,
            paymentReasonName: reviewData.fromPaymentReason,
            processingPriority: reviewData.fromProcessingPriority,
            label: 'From Account',
          }
        "
      ></ng-container>
      <div class="financial-accounts-icon"></div>
      <ng-container
        *ngTemplateOutlet="
          financialAccountView;
          context: {
            $implicit: reviewData.toAccount,
            method: reviewData.toMethod,
            achDetails: reviewData.toAchDetails,
            wireDetails: reviewData.toWireDetails,
            paymentReasonName: reviewData.toPaymentReason,
            processingPriority: reviewData.toProcessingPriority,
            label: 'To Account',
          }
        "
      ></ng-container>
    </div>
    <div class="separator-line"></div>
    <div class="additional-details">
      <div class="additional-details-title">
        {{ reviewData?.deliveryOption?.label }}
      </div>
      <div class="additional-details-sections">
        <div class="additional-details-inner">
          <div class="details-item">
            <div class="details-label">Payment Reason</div>
            <div class="details-value">{{ reviewData.toPaymentReason || reviewData.fromPaymentReason | notAvailable }}</div>
          </div>
          @if (reviewData.fromMethod === 'wire' && reviewData?.fromWireDetails; as wireDetails) {
            <div class="details-item">
              <div class="details-label">Narrative:</div>
              <div class="details-value">{{ wireDetails.description }}</div>
            </div>
            <div class="details-item">
              <div class="details-label">Memo:</div>
              <div class="details-value">
                @if (wireDetails.memo) {
                  <div>{{ wireDetails.memo }}</div>
                } @else {
                  <div class="empty-value">--</div>
                }
              </div>
            </div>
          }
          @if (
            (reviewData.toMethod === 'ach' && reviewData?.toAchDetails) || (reviewData.fromMethod === 'ach' && reviewData?.fromAchDetails);
            as achDetails
          ) {
            @if (achDetails.rkorACHEntryType) {
              <div class="details-item">
                <div class="details-label">SEC Code:</div>
                <div class="details-value">{{ achDetails.rkorACHEntryType }}</div>
              </div>
            }
            @if (achDetails.rkorACHIndividualId) {
              <div class="details-item">
                <div class="details-label">Individual ID:</div>
                <div class="details-value">{{ achDetails.rkorACHIndividualId }}</div>
              </div>
            }
            @if (achDetails.rkorACHCheckSerialNumber) {
              <div class="details-item">
                <div class="details-label">Check Serial Number:</div>
                <div class="details-value">{{ achDetails.rkorACHCheckSerialNumber }}</div>
              </div>
            }
            @if (achDetails.rkorACHTerminalCity) {
              <div class="details-item">
                <div class="details-label">Terminal City:</div>
                <div class="details-value">{{ achDetails.rkorACHTerminalCity }}</div>
              </div>
            }
            @if (achDetails.rkorACHTerminalState) {
              <div class="details-item">
                <div class="details-label">Terminal State:</div>
                <div class="details-value">{{ achDetails.rkorACHTerminalState }}</div>
              </div>
            }
            @if (reviewData?.debitAuthorization) {
              <div class="details-item">
                <div class="details-label">Debit Authorization:</div>
                <div class="details-value">
                  {{ reviewData.debitAuthorization?.authorizationLabel ?? 'Not Available' }}
                </div>
              </div>
            }
          }
          @if (reviewData.toMethod === 'wire' && reviewData?.toWireDetails; as wireDetails) {
            <div class="details-item">
              <div class="details-label">Narrative:</div>
              <div class="details-value">{{ wireDetails.description }}</div>
            </div>
            <div class="details-item">
              <div class="details-label">Memo:</div>
              <div class="details-value">
                @if (wireDetails.memo) {
                  <div>{{ wireDetails.memo }}</div>
                } @else {
                  <div class="empty-value">--</div>
                }
              </div>
            </div>
          }
        </div>
        <div class="additional-details-inner">
          <div class="details-item">
            <div class="details-label">Funds Delivery Estimate</div>
            <div class="details-value">
              {{ reviewData?.deliveryOption?.estLabelDay }} – {{ reviewData?.deliveryOption?.estLabelDate }}
              <div class="details-value-estimation">
                We estimate the funds will settle at the receiving institution in the
                <span class="strong">{{ reviewData?.deliveryOption?.estLabelTime }}</span> of
                <span class="strong">{{ reviewData?.deliveryOption?.estLabelDate }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="separator-line"></div>
    @if (reviewData.note) {
      <div class="notes">
        <div class="notes-label">Notes</div>
        <div class="notes-note">{{ reviewData.note }}</div>
      </div>
    }
    <div class="assignee-section">
      <app-assignee-select
        label="Assign Approver (Optional)"
        placeholder="Unassigned"
        [selectedAssignee]="selectedAssignee"
        [disabled]="isAssigning"
        (changed)="setSelectedAssignee($event)"
        (closed)="onAssigneeSelectClosed()"
      ></app-assignee-select>
    </div>
    <div class="separator-line"></div>
    <div class="buttons">
      <button rktButton btnType="secondary" title="Create new transaction" (click)="onCreateNewAfterApprovalClick()">
        Create new transaction
      </button>
      <button rktButton title="View Approval Request" (click)="onViewApprovalRequestClick()">View Approval Request</button>
    </div>
  } @else if (createdTransactionId) {
    <div>
      <div class="modal-icon" [ngClass]="'modal-icon-' + (reviewData.scheduleDateTime ? 'scheduled' : 'success')"></div>
      @if (reviewData.transactionType === 'MLT') {
        @if (isUpdate) {
          <div class="modal-title">Multi-Leg Transaction Updated!</div>
        }
        @if (!isUpdate) {
          <div class="modal-title">
            {{ reviewData?.scheduleDateTimeDisplayValue ? 'Multi-Leg Transaction Scheduled!' : 'Multi-Leg Transaction Created!' }}
          </div>
        }
      }
      @if (reviewData.transactionType === 'SLT') {
        @if (isUpdate) {
          <div class="modal-title">Transaction Updated!</div>
        }
        @if (!isUpdate) {
          <div class="modal-title">
            {{ reviewData?.scheduleDateTimeDisplayValue ? 'Transaction Scheduled!' : 'Transaction Created!' }}
          </div>
        }
      }
      <div class="transaction-id">Transaction # {{ createdTransactionId | rktShortId }}</div>
    </div>
    <div class="separator-line"></div>
    <div class="amount">
      <ng-container>
        @if (reviewData.amountOption === 'EXACT_AMOUNT') {
          <div class="label">Amount</div>
          <div class="value">
            {{ reviewData.amount || '' | amount: reviewData.currency || undefined }}
          </div>
        }
        @if (reviewData.amountOption === 'PERCENTAGE') {
          <div class="label">Percentage</div>
          <div class="value">{{ reviewData.amount }}%</div>
        }
      </ng-container>
      @if (reviewData.name) {
        <div class="scheduler-name">
          {{ reviewData.name }}
        </div>
      }
    </div>
    @if (reviewData.scheduleDateTime) {
      <div class="schedule-preview">
        <div class="scheduled-for">
          <div class="schedule-icon"></div>
          <div class="scheduled-for-label">Scheduled for:</div>
          <span class="scheduled-date-time">{{ reviewData.scheduleDateTimeDisplayValue }}</span>
        </div>
        @if (reviewData.scheduleRecurringDisplayValue; as scheduleRecurring) {
          <div class="schedule-recurring">Recurrence: {{ scheduleRecurring }}</div>
        }
      </div>
    }
    <div class="financial-accounts-container">
      <ng-container
        *ngTemplateOutlet="
          financialAccountView;
          context: {
            $implicit: reviewData.fromAccount,
            method: reviewData.fromMethod,
            achDetails: reviewData.fromAchDetails,
            wireDetails: reviewData.fromWireDetails,
            paymentReasonName: reviewData.fromPaymentReason,
            processingPriority: reviewData.fromProcessingPriority,
            label: 'From Account',
          }
        "
      ></ng-container>
      <div class="financial-accounts-icon"></div>
      <ng-container
        *ngTemplateOutlet="
          financialAccountView;
          context: {
            $implicit: reviewData.toAccount,
            method: reviewData.toMethod,
            achDetails: reviewData.toAchDetails,
            wireDetails: reviewData.toWireDetails,
            paymentReasonName: reviewData.toPaymentReason,
            processingPriority: reviewData.toProcessingPriority,
            label: 'To Account',
          }
        "
      ></ng-container>
    </div>
    <div class="separator-line"></div>
    <div class="additional-details">
      <div class="additional-details-title">
        {{ reviewData?.deliveryOption?.label }}
      </div>
      <div class="additional-details-sections">
        <div class="additional-details-inner">
          <div class="details-item">
            <div class="details-label">Payment Reason</div>
            <div class="details-value">{{ reviewData.toPaymentReason || reviewData.fromPaymentReason | notAvailable }}</div>
          </div>
          @if (reviewData.fromMethod === 'wire' && reviewData?.fromWireDetails; as wireDetails) {
            <div class="details-item">
              <div class="details-label">Narrative:</div>
              <div class="details-value">{{ wireDetails.description }}</div>
            </div>
            <div class="details-item">
              <div class="details-label">Memo:</div>
              <div class="details-value">
                @if (wireDetails.memo) {
                  <div>{{ wireDetails.memo }}</div>
                } @else {
                  <div class="empty-value">--</div>
                }
              </div>
            </div>
          }
          @if (
            (reviewData.toMethod === 'ach' && reviewData?.toAchDetails) || (reviewData.fromMethod === 'ach' && reviewData?.fromAchDetails);
            as achDetails
          ) {
            @if (achDetails.rkorACHEntryType) {
              <div class="details-item">
                <div class="details-label">SEC Code:</div>
                <div class="details-value">{{ achDetails.rkorACHEntryType }}</div>
              </div>
            }
            @if (achDetails.rkorACHIndividualId) {
              <div class="details-item">
                <div class="details-label">Individual ID:</div>
                <div class="details-value">{{ achDetails.rkorACHIndividualId }}</div>
              </div>
            }
            @if (achDetails.rkorACHCheckSerialNumber) {
              <div class="details-item">
                <div class="details-label">Check Serial Number:</div>
                <div class="details-value">{{ achDetails.rkorACHCheckSerialNumber }}</div>
              </div>
            }
            @if (achDetails.rkorACHTerminalCity) {
              <div class="details-item">
                <div class="details-label">Terminal City:</div>
                <div class="details-value">{{ achDetails.rkorACHTerminalCity }}</div>
              </div>
            }
            @if (achDetails.rkorACHTerminalState) {
              <div class="details-item">
                <div class="details-label">Terminal State:</div>
                <div class="details-value">{{ achDetails.rkorACHTerminalState }}</div>
              </div>
            }
            @if (reviewData?.debitAuthorization) {
              <div class="details-item">
                <div class="details-label">Debit Authorization:</div>
                <div class="details-value">
                  {{ reviewData.debitAuthorization?.authorizationLabel ?? 'Not Available' }}
                </div>
              </div>
            }
          }
          @if (reviewData.toMethod === 'wire' && reviewData?.toWireDetails; as wireDetails) {
            <div class="details-item">
              <div class="details-label">Narrative:</div>
              <div class="details-value">{{ wireDetails.description }}</div>
            </div>
            <div class="details-item">
              <div class="details-label">Memo:</div>
              <div class="details-value">
                @if (wireDetails.memo) {
                  <div>{{ wireDetails.memo }}</div>
                } @else {
                  <div class="empty-value">--</div>
                }
              </div>
            </div>
          }
        </div>
        <div class="additional-details-inner">
          <div class="details-item">
            <div class="details-label">Funds Delivery Estimate</div>
            <div class="details-value">
              {{ reviewData?.deliveryOption?.estLabelDay }} – {{ reviewData?.deliveryOption?.estLabelDate }}
              <div class="details-value-estimation">
                We estimate the funds will settle at the receiving institution in the
                <span class="strong">{{ reviewData?.deliveryOption?.estLabelTime }}</span> of
                <span class="strong">{{ reviewData?.deliveryOption?.estLabelDate }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="separator-line"></div>
    @if (reviewData.note) {
      <div class="notes">
        <div class="notes-label">Notes</div>
        <div class="notes-note">{{ reviewData.note }}</div>
      </div>
    }
    <div class="buttons" [class.editing]="isUpdate">
      @if (!isUpdate) {
        <button rktButton btnType="secondary" title="Create new transaction" (click)="onCreateNewTransactionBtnClick()">
          Create new transaction
        </button>
      }
      <button rktButton title="Done" (click)="onDoneBtnClick(reviewData.transactionType === 'MLT', !!reviewData.scheduleDateTime)">
        Done
      </button>
    </div>
  } @else {
    <div class="review-header">
      <div class="review-title">Review the transaction</div>
      @if (reviewData.transactionType === 'MLT') {
        <div class="multi-leg-title">
          <div class="multi-leg-title-icon"></div>
          This is a Multi-Leg Transaction
        </div>
      }
    </div>
    <div class="separator-line"></div>
    <div class="amount">
      <ng-container>
        @if (reviewData.amountOption === 'EXACT_AMOUNT') {
          <div class="label">Amount</div>
          <div class="value">
            {{ reviewData.amount || '' | amount: reviewData.currency || undefined }}
          </div>
        }
        @if (reviewData.amountOption === 'PERCENTAGE') {
          <div class="label">Percentage</div>
          <div class="value">{{ reviewData.amount }}%</div>
        }
      </ng-container>
      @if (reviewData.name) {
        <div class="scheduler-name">
          {{ reviewData.name }}
        </div>
      }
    </div>
    @if (reviewData.scheduleDateTime) {
      <div class="schedule-preview">
        <div class="scheduled-for">
          <div class="schedule-icon"></div>
          <div class="scheduled-for-label">Scheduled for:</div>
          <span class="scheduled-date-time">{{ reviewData.scheduleDateTimeDisplayValue }}</span>
        </div>
        @if (reviewData.scheduleRecurringDisplayValue; as scheduleRecurring) {
          <div class="schedule-recurring">Recurrence: {{ scheduleRecurring }}</div>
        }
      </div>
    }
    <div class="financial-accounts-container">
      <ng-container
        *ngTemplateOutlet="
          financialAccountView;
          context: {
            $implicit: reviewData.fromAccount,
            method: reviewData.fromMethod,
            achDetails: reviewData.fromAchDetails,
            wireDetails: reviewData.fromWireDetails,
            paymentReasonName: reviewData.fromPaymentReason,
            processingPriority: reviewData.fromProcessingPriority,
            label: 'From Account',
          }
        "
      ></ng-container>
      <div class="financial-accounts-icon"></div>
      <ng-container
        *ngTemplateOutlet="
          financialAccountView;
          context: {
            $implicit: reviewData.toAccount,
            method: reviewData.toMethod,
            achDetails: reviewData.toAchDetails,
            wireDetails: reviewData.toWireDetails,
            paymentReasonName: reviewData.toPaymentReason,
            processingPriority: reviewData.toProcessingPriority,
            label: 'To Account',
          }
        "
      ></ng-container>
    </div>
    <div class="separator-line"></div>
    <div class="additional-details">
      <div class="additional-details-title">
        {{ reviewData?.deliveryOption?.label }}
      </div>
      <div class="additional-details-sections">
        <div class="additional-details-inner">
          <div class="details-item">
            <div class="details-label">Payment Reason</div>
            <div class="details-value">{{ reviewData.toPaymentReason || reviewData.fromPaymentReason | notAvailable }}</div>
          </div>
          @if (reviewData.fromMethod === 'wire' && reviewData?.fromWireDetails; as wireDetails) {
            <div class="details-item">
              <div class="details-label">Narrative:</div>
              <div class="details-value">{{ wireDetails.description }}</div>
            </div>
            <div class="details-item">
              <div class="details-label">Memo:</div>
              <div class="details-value">
                @if (wireDetails.memo) {
                  <div>{{ wireDetails.memo }}</div>
                } @else {
                  <div class="empty-value">--</div>
                }
              </div>
            </div>
          }
          @if (
            (reviewData.toMethod === 'ach' && reviewData?.toAchDetails) || (reviewData.fromMethod === 'ach' && reviewData?.fromAchDetails);
            as achDetails
          ) {
            @if (achDetails.rkorACHEntryType) {
              <div class="details-item">
                <div class="details-label">SEC Code:</div>
                <div class="details-value">{{ achDetails.rkorACHEntryType }}</div>
              </div>
            }
            @if (achDetails.rkorACHIndividualId) {
              <div class="details-item">
                <div class="details-label">Individual ID:</div>
                <div class="details-value">{{ achDetails.rkorACHIndividualId }}</div>
              </div>
            }
            @if (achDetails.rkorACHCheckSerialNumber) {
              <div class="details-item">
                <div class="details-label">Check Serial Number:</div>
                <div class="details-value">{{ achDetails.rkorACHCheckSerialNumber }}</div>
              </div>
            }
            @if (achDetails.rkorACHTerminalCity) {
              <div class="details-item">
                <div class="details-label">Terminal City:</div>
                <div class="details-value">{{ achDetails.rkorACHTerminalCity }}</div>
              </div>
            }
            @if (achDetails.rkorACHTerminalState) {
              <div class="details-item">
                <div class="details-label">Terminal State:</div>
                <div class="details-value">{{ achDetails.rkorACHTerminalState }}</div>
              </div>
            }
            @if (reviewData?.debitAuthorization) {
              <div class="details-item">
                <div class="details-label">Debit Authorization:</div>
                <div class="details-value">
                  {{ reviewData.debitAuthorization?.authorizationLabel ?? 'Not Available' }}
                </div>
              </div>
            }
          }
          @if (reviewData.toMethod === 'wire' && reviewData?.toWireDetails; as wireDetails) {
            <div class="details-item">
              <div class="details-label">Narrative:</div>
              <div class="details-value">{{ wireDetails.description }}</div>
            </div>
            <div class="details-item">
              <div class="details-label">Memo:</div>
              <div class="details-value">
                @if (wireDetails.memo) {
                  <div>{{ wireDetails.memo }}</div>
                } @else {
                  <div class="empty-value">--</div>
                }
              </div>
            </div>
          }
          @if (reviewData.selectedFundOnHold; as foh) {
            <div class="details-item">
              <div class="details-label">Fund on Hold:</div>
              <div class="details-value">
                <div>${{ foh.amount }}</div>
                @if (foh.memo) {
                  <div class="foh-memo">{{ foh.memo }}</div>
                }
              </div>
            </div>
          }
        </div>
        <div class="additional-details-inner">
          <div class="details-item">
            <div class="details-label">Funds Delivery Estimate</div>
            <div class="details-value">
              {{ reviewData?.deliveryOption?.estLabelDay }} – {{ reviewData?.deliveryOption?.estLabelDate }}
              <div class="details-value-estimation">
                We estimate the funds will settle at the receiving institution in the
                <span class="strong">{{ reviewData?.deliveryOption?.estLabelTime }}</span> of
                <span class="strong">{{ reviewData?.deliveryOption?.estLabelDate }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="separator-line"></div>
    @if (reviewData.note) {
      <div class="notes">
        <div class="notes-label">Notes</div>
        <div class="notes-note">{{ reviewData.note }}</div>
      </div>
    }
    <div class="buttons">
      <button rktButton btnType="secondary" title="Back to Edit" (click)="activeModal.close({ action: 'EDIT' })">Back to Edit</button>
      <button rktButton title="Confirm" title="Confirm" (click)="onSubmit()" [disabled]="submitLoading" [class.loading]="submitLoading">
        <span class="btn-text">Confirm</span>
        @if (submitLoading) {
          <span class="btn-spinner"><span class="spinner-grow" role="status" aria-hidden="true"></span></span>
        }
      </button>
    </div>
  }
}

<ng-template
  #financialAccountView
  let-financialAccount
  let-method="method"
  let-achDetails="achDetails"
  let-wireDetails="wireDetails"
  let-paymentReasonName="paymentReasonName"
  let-processingPriority="processingPriority"
  let-label="label"
>
  <div class="financial-account">
    <div class="financial-account-title">{{ label }}</div>
    @if (financialAccount) {
      <div class="rows">
        <div class="row">
          <div class="label">Account Holder:</div>
          <div class="value">
            <div [class.empty-value]="!(financialAccount.accountHolderInfo || '' | rktSplit: '::' : 0)">
              {{ financialAccount.accountHolderInfo || '' | rktSplit: '::' : 0 | notAvailable: '--' }}
            </div>
            <div [class.empty-value]="!(financialAccount.accountHolderInfo || '' | rktSplit: '::' : 1)">
              {{ financialAccount.accountHolderInfo || '' | rktSplit: '::' : 1 | notAvailable: '--' }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="label">Account Info:</div>
          <div class="value">
            <div [class.empty-value]="!(financialAccount.accountInfo || '' | rktSplit: '::' : 0)">
              {{ financialAccount.accountInfo || '' | rktSplit: '::' : 0 | notAvailable: '--' }}
            </div>
            <div [class.empty-value]="!(financialAccount.accountInfo || '' | rktSplit: '::' : 1)">
              {{ financialAccount.accountInfo || '' | rktSplit: '::' : 1 | notAvailable: '--' }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="label">Account Category:</div>
          <div class="value">{{ financialAccount.accountCategory }}</div>
        </div>
        @if (financialAccount.accountCategory !== 'EXTERNAL') {
          <div class="row">
            <div class="label">Available Balance:</div>
            <div class="value">
              @if (financialAccount.availableBalance !== undefined) {
                <div>
                  {{ financialAccount.currency | currencySign }}
                  {{ (financialAccount.availableBalance | number: '1.2-2') || '' | mask: 'separator' : { thousandSeparator: ',' } }}
                </div>
              } @else {
                <div class="empty-value">--</div>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>
</ng-template>
